import { computeSOWCosts } from './computeSOWCosts';

export const fmt = (n, decimals = 0) =>
  n == null || isNaN(n)
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(n);

export const pct = (n, decimals = 1) =>
  n == null || isNaN(n) ? '—' : `${Number(n).toFixed(decimals)}%`;

export const num = v => (v === '' || v == null ? null : Number(v));

/** Days remaining in the month AFTER the purchase date (closing day to EOM inclusive) */
function daysRemainingInMonth(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr + 'T12:00:00'); // noon avoids DST issues
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return daysInMonth - d.getDate(); // days after closing day through end of month
}

/** Compute full P&L from a deal object (all fields + cost arrays) */
export function computePL(deal) {
  const arv           = deal.arv || 0;
  const purchasePrice = deal.purchase_price || 0;

  // ── Rehab — stable union: computed live estimates + DB budgeted overrides ──
  //
  // Strategy:
  //   1. computeSOWCosts(deal) → live estimates from deal fields (no DB dependency)
  //   2. sow_costs DB rows     → user's budgeted/actual values (persisted)
  //   3. Union of both: prefer DB budgeted where it exists, else use live estimate
  //      This also picks up DB rows whose trigger conditions no longer compute
  //      (e.g. user cleared a field but left a budgeted amount).

  const computedLines = computeSOWCosts(deal);
  const sowDbRows     = deal.sow_costs || [];

  // Map: key → live estimated amount
  const computedMap = {};
  for (const line of computedLines) {
    computedMap[`${line.section}|||${line.label}`] = line.estimated;
  }

  // Map: key → DB budgeted (prefer) or DB estimated
  const dbMap = {};
  for (const row of sowDbRows) {
    const val = row.budgeted ?? row.estimated ?? 0;
    if (val > 0) dbMap[`${row.section}|||${row.label}`] = val;
  }

  // Union of all keys → sum
  const allKeys = new Set([...Object.keys(computedMap), ...Object.keys(dbMap)]);
  let rehabBase = 0;
  for (const key of allKeys) {
    rehabBase += key in dbMap ? dbMap[key] : (computedMap[key] || 0);
  }

  // Fallback: if absolutely no SOW data anywhere, use manual entry
  if (allKeys.size === 0) {
    rehabBase = deal.rehab_budget_manual || 0;
  }

  // Actuals and display-only budgeted total from DB rows
  const sowActual   = sowDbRows.reduce((s, r) => s + (r.actual || 0), 0);
  const sowBudgeted = sowDbRows.reduce((s, r) => s + (r.budgeted ?? r.estimated ?? 0), 0);

  const contingency = rehabBase * ((deal.contingency_pct || 10) / 100);
  const rehabTotal  = rehabBase + contingency;

  // ── Front-end costs ───────────────────────────────────────────────────────
  const feItems    = deal.frontend_costs || [];
  const feBudgeted = feItems.reduce((s, r) => s + (r.budgeted || 0), 0);
  const feActual   = feItems.reduce((s, r) => s + (r.actual  || 0), 0);
  const feBenchmark = arv * ((deal.frontend_pct || 4) / 100);

  // ── Back-end costs ────────────────────────────────────────────────────────
  const beItems    = deal.backend_costs || [];
  const beBudgeted = beItems.reduce((s, r) => s + (r.budgeted || 0), 0);
  const beActual   = beItems.reduce((s, r) => s + (r.actual  || 0), 0);
  const beBenchmark = arv * ((deal.backend_pct || 9.25) / 100);

  // ── Holding costs ─────────────────────────────────────────────────────────
  const hcItems    = deal.holding_costs || [];
  const hcBudgeted = hcItems.reduce((s, r) => s + (r.budgeted || 0), 0);
  const hcActual   = hcItems.reduce((s, r) => s + (r.actual  || 0), 0);
  const hcBenchmark = arv * ((deal.holding_pct_per_month || 1) / 100) * (deal.hold_months || 6);

  // ── Capital partner payout ────────────────────────────────────────────────
  let cpPayout = 0;
  if (deal.has_partner) {
    const grossProfit = arv - purchasePrice - rehabTotal - feBudgeted - beBudgeted - hcBudgeted;
    if (deal.cp_return_type === 'flat') {
      cpPayout = deal.cp_return_value || 0;
    } else if (deal.cp_return_type === 'pct_profit') {
      cpPayout = grossProfit * ((deal.cp_return_value || 0) / 100);
    } else if (deal.cp_return_type === 'preferred_return') {
      cpPayout = (deal.cp_contribution || 0) * ((deal.cp_return_value || 0) / 100);
    }
  }

  // ── MAO = ARV - profit% - backend% - frontend% - rehab - holding ─────────
  const profitTarget = arv * ((deal.profit_pct || 20) / 100);
  const mao = arv - profitTarget - beBenchmark - feBenchmark - hcBenchmark - rehabTotal;

  // ── Projected P&L (budgeted) ──────────────────────────────────────────────
  const totalCostsBudgeted = purchasePrice + feBudgeted + rehabTotal + hcBudgeted + beBudgeted + cpPayout;
  const netBudgeted        = arv - totalCostsBudgeted;
  const profitPctBudgeted  = arv ? (netBudgeted / arv) * 100 : 0;
  const roiBudgeted        = totalCostsBudgeted ? (netBudgeted / totalCostsBudgeted) * 100 : 0;

  // ── Actual P&L ────────────────────────────────────────────────────────────
  const salePrice        = deal.actual_sale_price || arv;
  const totalCostsActual = purchasePrice + feActual + rehabTotal + hcActual + beActual + cpPayout;
  const netActual        = salePrice - totalCostsActual;
  const profitPctActual  = salePrice ? (netActual / salePrice) * 100 : 0;

  // ── 3 Shacks / Hard Money Loan ────────────────────────────────────────────
  // loan = (90% × PP) + rehab — 75% ARV is a qualification check only, NOT a cap
  const loan        = (0.90 * purchasePrice) + rehabBase;
  const arvCheck75  = arv * 0.75;
  const arvQualOk   = (loan <= arvCheck75) || (arv === 0); // passes if loan <= 75% ARV or no ARV yet

  // ── Cash to Close ─────────────────────────────────────────────────────────
  const downPayment  = purchasePrice * 0.10;
  const titleCosts   = purchasePrice * 0.03;
  const points       = loan * ((deal.hm_points || 2) / 100);
  const insurance    = deal.hm_insurance ?? 2500;
  const monthlyRate  = (deal.hm_rate || 12) / 100 / 12; // 12%/yr = 1%/mo
  const daysLeft     = daysRemainingInMonth(deal.purchase_date);
  const daysInMonth  = deal.purchase_date
    ? new Date(new Date(deal.purchase_date + 'T12:00:00').getFullYear(),
               new Date(deal.purchase_date + 'T12:00:00').getMonth() + 1, 0).getDate()
    : 30;
  const prepaidInterest = loan * monthlyRate * (daysLeft / daysInMonth);
  const cashToClose     = downPayment + titleCosts + points + insurance + prepaidInterest;

  // ── Cash on Cash Return ───────────────────────────────────────────────────
  const cocReturn = cashToClose > 0 ? (netBudgeted / cashToClose) * 100 : 0;

  return {
    arv, purchasePrice,
    rehabBase, contingency, rehabTotal,
    sowBudgeted, sowActual,
    feBudgeted, feActual, feBenchmark,
    beBudgeted, beActual, beBenchmark,
    hcBudgeted, hcActual, hcBenchmark,
    cpPayout,
    mao,
    profitTarget,
    totalCostsBudgeted, netBudgeted, profitPctBudgeted, roiBudgeted,
    salePrice, totalCostsActual, netActual, profitPctActual,
    // Lender / cash-to-close
    loan, arvCheck75, arvQualOk,
    downPayment, titleCosts, points, insurance, prepaidInterest, daysLeft,
    cashToClose, cocReturn,
  };
}
